import { CheckOutlined, CloudDownloadOutlined, FormOutlined, MailOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Col, Descriptions, DescriptionsProps, Row, Space, Tooltip } from 'antd';
import FileSaver from 'file-saver';
import { useContext, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import receivableNotificationWrongAddressApi from '../../../../apis/ReceivableNotificationWrongAddressApi';
import { Receivable, ReceivableNotificationWrongAddress } from '../../../../models/Entities';
import alertService from '../../../../services/AlertService';
import ReceivableCancellationModal from '../ReceivableCancellationModal/ReceivableCancellationModal';
import ReceivableNotificationBuildModal from '../ReceivableNotificationBuildModal/ReceivableNotificationBuildModal';
import ReceivablePaymentModal from '../ReceivablePaymentModal/ReceivablePaymentModal';
import styles from './ReceivableNotificationWrongAddressComponent.module.scss';
import CustomContext from '../../../../contexts/CustomContext';
import rolesService from '../../../../services/RolesService';

const ReceivableNotificationWrongAddressComponent = (props: Props) => {
    const { receivable, notificationWrongAddress, onUpdate, readonly } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const { auth } = useContext(CustomContext);
    const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);
    const [loading, setLoading] = useState<'downloading'>();
    const [cancellationModalVisible, setCancellationModalVisible] = useState<boolean>(false);
    const [notificationBuildModalVisible, setNotificationBuildModalVisible] = useState<boolean>(false);

    /*** EFFECTS ***/

    /*** METHODS ***/

    const save = async () => {
        try {
            setPaymentModalVisible(false);
            setNotificationBuildModalVisible(false);
            setCancellationModalVisible(false);
            await onUpdate();
        } catch (error) {
            alertService.displayError(error, intl);
        }
    };

    const download = async () => {
        try {
            setLoading('downloading');
            const document = await receivableNotificationWrongAddressApi.download(notificationWrongAddress.id!);
            FileSaver.saveAs(document, notificationWrongAddress.file?.name);
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    const isAdmin = rolesService.hasAnyRole(auth, ['ROLE_ADMIN']);
    const items: DescriptionsProps['items'] = [
        {
            key: 'amount',
            label: <FormattedMessage id="receivable.amount.principal" />,
            children: receivable?.amount !== undefined && (
                <FormattedNumber
                    value={receivable?.amount.principal!}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    style={'currency' as any}
                    currency="EUR"
                />
            )
        },
        {
            key: 'surcharge',
            label: <FormattedMessage id="receivable.amount.surcharge" />,
            children: receivable?.amount?.surcharge !== undefined && (
                <FormattedNumber
                    value={receivable?.amount.surcharge}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    style={'currency' as any}
                    currency="EUR"
                />
            )
        },
        {
            key: 'penalty',
            label: <FormattedMessage id="receivable.amount.costs" />,
            children: receivable?.amount?.costs !== undefined && (
                <FormattedNumber
                    value={receivable?.amount.costs}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    style={'currency' as any}
                    currency="EUR"
                />
            )
        },
        {
            key: 'totalAmount',
            label: <FormattedMessage id="receivable.totalAmount" />,
            children: receivable?.amount?.total !== undefined && (
                <strong>
                    <FormattedNumber
                        value={receivable?.amount?.total}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                        style={'currency' as any}
                        currency="EUR"
                    />
                </strong>
            )
        },
        {
            key: 'created',
            label: <FormattedMessage id="audit.created" />,
            children: notificationWrongAddress.audit && (
                <FormattedDate
                    value={notificationWrongAddress.audit.updated as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                />
            )
        }
    ];

    return (
        <>
            <Row gutter={[28, 0]}>
                <Col span={24}>
                    <Descriptions layout="horizontal" items={items} size="default" column={4} />
                </Col>
            </Row>

            <Row gutter={[28, 0]} className={styles.buttons}>
                <Col span={24}>
                    <Space>
                        {isAdmin && !readonly && (
                            <>
                                <Tooltip title={<FormattedMessage id="receivable.paid.tooltip" />} mouseLeaveDelay={0}>
                                    <Button type="primary" danger icon={<CheckOutlined />} onClick={() => setPaymentModalVisible(true)}>
                                        <FormattedMessage id="receivable.paid" tagName="span" />
                                    </Button>
                                </Tooltip>
                                <Link to={`/taxpayers/${receivable.taxpayer?.id}`}>
                                    <Button type="primary" icon={<FormOutlined />}>
                                        <FormattedMessage id="taxpayer.modify.data" />
                                    </Button>
                                </Link>
                                <Button type="primary" icon={<MailOutlined />} onClick={() => setNotificationBuildModalVisible(true)}>
                                    <FormattedMessage id="receivable.registration.notificationBuild" tagName="span" />
                                </Button>
                                <Button icon={<UndoOutlined />} onClick={() => setCancellationModalVisible(true)}>
                                    <FormattedMessage id="receivable.cancellation.button" tagName="span" />
                                </Button>
                            </>
                        )}
                        <Button icon={<CloudDownloadOutlined />} onClick={download} loading={loading === 'downloading'}>
                            <FormattedMessage id="button.download" tagName="span" />
                        </Button>
                    </Space>
                </Col>
            </Row>

            {paymentModalVisible && receivable && (
                <ReceivablePaymentModal receivable={receivable} onCancel={() => setPaymentModalVisible(false)} onSave={save} />
            )}
            {notificationBuildModalVisible && receivable && (
                <ReceivableNotificationBuildModal receivable={receivable} onCancel={() => setNotificationBuildModalVisible(false)} onSave={save} />
            )}
            {cancellationModalVisible && (
                <ReceivableCancellationModal receivable={receivable} onCancel={() => setCancellationModalVisible(false)} onSave={save} />
            )}
        </>
    );
};

export default ReceivableNotificationWrongAddressComponent;

interface Props {
    receivable: Receivable;
    notificationWrongAddress: ReceivableNotificationWrongAddress;
    onUpdate: () => Promise<void>;
    readonly: boolean;
}
